import React, { useState } from 'react';
import { shopConfig as cfg } from '../shopConfig';
import { hourRows } from '../hours';

const EMAIL = 'redrobinboutique@yahoo.com';
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${cfg.contact.maps_destination}`;
const EMBED_URL = `https://www.google.com/maps?q=${cfg.contact.maps_destination}&output=embed`;

// Click-to-load map. The iframe is NOT mounted until the visitor asks for it, so
// no visitor IP or cookie reaches Google on page load - consistent with the cookie
// policy, and it keeps the page light. Phase-3: lift to a shared <MapBlock/>.
const MapBlock: React.FC = () => {
  const [show, setShow] = useState(false);
  if (show) {
    return (
      <iframe
        title="Red Robin Boutique location"
        src={EMBED_URL}
        style={{ width: '100%', height: 340, border: 0, display: 'block' }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    );
  }
  return (
    <div style={{ width: '100%', height: 340, background: 'rgba(0,0,0,.05)', border: '1px solid rgba(0,0,0,.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 20, textAlign: 'center' }}>
      <svg width="34" height="34" viewBox="0 0 24 24" fill={cfg.brand.accent}>
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z" />
      </svg>
      <div style={{ fontSize: 14, color: 'rgba(0,0,0,.7)', lineHeight: 1.6 }}>
        Main Street, Ballinagh, Co. Cavan
      </div>
      <button
        onClick={() => setShow(true)}
        style={{ background: 'none', border: `1px solid ${cfg.brand.accent}`, color: cfg.brand.secondary, padding: '9px 20px', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}>
        Show map
      </button>
      <div style={{ fontSize: 11, color: 'rgba(0,0,0,.4)', maxWidth: 320 }}>
        Loading the map connects you to Google Maps.
      </div>
    </div>
  );
};

export const ContactPage: React.FC = () => (
  <div style={{ maxWidth: 1000, margin: '0 auto', padding: '48px 20px 64px' }}>
    <h1 style={{ fontSize: 30, fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: '0 0 6px' }}>
      Contact Us
    </h1>
    <p style={{ fontSize: 15, color: 'rgba(0,0,0,.55)', margin: '0 0 36px' }}>
      Red Robin Boutique, Ballinagh
    </p>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 40 }}>
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: '0 0 12px' }}>Visit the shop</h2>
        <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(0,0,0,.72)', margin: '0 0 8px' }}>
          {cfg.contact.address_lines.map((l) => (<React.Fragment key={l}>{l}<br /></React.Fragment>))}
        </p>
        <p style={{ fontSize: 14, color: 'rgba(0,0,0,.55)', margin: '0 0 18px' }}>
          Parking is available behind the building.
        </p>
        <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: cfg.brand.accent, color: cfg.brand.secondary, padding: '13px 24px', fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', textDecoration: 'none' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z" />
          </svg>
          Bring me there
        </a>

        <h2 style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: '34px 0 12px' }}>Call or email</h2>
        <p style={{ fontSize: 15, lineHeight: 1.9, margin: 0 }}>
          <a href={`tel:${cfg.contact.phone}`} style={{ color: cfg.brand.secondary }}>{cfg.contact.phone}</a><br />
          <a href={`mailto:${EMAIL}`} style={{ color: cfg.brand.secondary }}>{EMAIL}</a>
        </p>

        <h2 style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: '34px 0 12px' }}>Delivery</h2>
        <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(0,0,0,.72)', margin: 0 }}>
          We deliver across Ireland, the UK and worldwide. See our{' '}
          <a href="/shipping" style={{ color: cfg.brand.secondary }}>Shipping and Returns</a> page for times and costs.
        </p>
      </div>

      <div>
        <h2 style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: '0 0 12px' }}>Opening hours</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15, marginBottom: 28 }}>
          <tbody>
            {hourRows().map((h) => (
              <tr key={h.day} style={{ borderBottom: '1px solid rgba(0,0,0,.08)', background: h.today ? 'rgba(0,0,0,.035)' : 'transparent' }}>
                <td style={{ padding: '10px 8px', fontWeight: h.today ? 800 : 700, color: cfg.brand.secondary }}>{h.day}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right', color: h.closed ? 'rgba(0,0,0,.4)' : 'rgba(0,0,0,.72)' }}>{h.text}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <MapBlock />
      </div>
    </div>
  </div>
);